import { Button, Group, UnstyledButton } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { Trash } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import LanguageSelect from '@/components/LanguageSelect';
import NavigateToPartnerCta from '@/components/NavigateToPartnerCta';
import { PATHS, runtimeConfig } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import useQueryParams from '@/hooks/useQueryParams.ts';
import { normalizeRoutePath } from '@/routes.tsx';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training.ts';
import useAppStore from '@/store/app.ts';
import { AgentCreationStep } from '@/types/agent-training.ts';
// utils
import { cn } from '@/utils';

interface HeaderProps {
	className?: string;
}

const Header = ({ className }: HeaderProps) => {
	const logoSrc =
		partnerMetadata.getDarkLogoUrl(true) || partnerMetadata.getDarkLogoUrl();
	const { step, resetState: resetTrainingState } = useAgentTraining();
	const resetState = useAppStore((s) => s.resetState);
	const {
		enableMiniMode,
		isMiniCreatorFeatureEnabled,
		isOverlayMode,
		isPageMode,
		isProcessStarted,
		localization
	} = useHostContext();
	const { width: viewportW } = useViewportSize();
	const isMobile = viewportW < 768;
	const {
		params: { config: queryConfig }
	} = useQueryParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isAcronisVariant = partnerMetadata.getIsAcronisVariant();
	const [searchParams, setSearchParams] = useSearchParams();
	const initialParamsRef = useRef(Object.fromEntries(searchParams.entries()));
	const handleReset = useCallback(() => {
		agentTrainingRealtime.disconnect();
		resetState();
		resetTrainingState();
		if (Object.keys(initialParamsRef.current).length > 0) {
			setSearchParams(initialParamsRef.current, { replace: true });
		}
	}, [resetState, resetTrainingState, setSearchParams]);

	const isEmbeddedRemotePage = isPageMode && runtimeConfig.isRemoteBuild;
	const showPartnerCta = !isOverlayMode && !isEmbeddedRemotePage;

	const isFirstStep = runtimeConfig.isRemoteBuild
		? step === AgentCreationStep.SOURCE_SELECT
		: [
				normalizeRoutePath(PATHS.index),
				normalizeRoutePath(PATHS.creatorIndex),
				PATHS.remoteCreatorIndex
			].includes(location.pathname);

	const handleOpenConfirmationModal = useCallback(
		(onConfirm: () => void) => {
			modals.openConfirmModal({
				styles: {
					root: { '--mb-z-index': 1004 },
					inner: { '--mb-z-index': 1004 },
					overlay: { '--mb-z-index': 1004 }
				},
				title: localization.translate(
					'creator',
					'agentResetConfirmation.title'
				),
				labels: {
					confirm: localization.translate(
						'creator',
						'agentResetConfirmation.actions.confirm'
					),
					cancel: localization.translate(
						'creator',
						'agentResetConfirmation.actions.cancel'
					)
				},
				children: localization.translate(
					'creator',
					'agentResetConfirmation.description'
				),
				onConfirm
			});
		},
		[localization]
	);

	const handleNavigate = () => {
		if (isFirstStep) {
			return;
		}

		if (!runtimeConfig.isRemoteBuild) {
			handleOpenConfirmationModal(() => {
				navigate(PATHS.index);
				handleReset();
			});
			return;
		}
		navigate(PATHS.index);
	};

	const onBeforeRedirectToPartner = () => {
		if (
			runtimeConfig.isRemoteBuild &&
			isMiniCreatorFeatureEnabled &&
			isProcessStarted
		) {
			enableMiniMode?.();
		}
	};

	useEffect(() => {
		if (!queryConfig) {
			return;
		}

		try {
			const decoded = atob(queryConfig);
			const parsed = JSON.parse(decoded);

			if (parsed?.resetProcess) {
				const nextParams = new URLSearchParams(location.search);
				nextParams.delete('config');
				handleReset();
				setSearchParams(nextParams);
			}
		} catch {
			console.warn('Invalid config query param');
		}
	}, [queryConfig, handleReset, setSearchParams, location.search]);

	if (!logoSrc) {
		console.error('No logo configured for partner:', partnerMetadata.getId());
	}

	return (
		<header
			className={cn(
				'flex gap-5 w-full py-8 m-0! align-middle content-center items-center',
				isAcronisVariant ? 'relative justify-end h-36' : 'justify-between',
				{ 'py-3': isOverlayMode },
				className
			)}
		>
			{logoSrc && (
				<Button
					className={cn({
						'cursor-default pointer-events-none': isFirstStep,
						'absolute left-1/2 -translate-x-1/2': isAcronisVariant
					})}
					h="auto"
					onClick={handleNavigate}
					p={8}
					variant="transparent"
				>
					<img
						alt={`${partnerMetadata.getName()} Logo`}
						className="max-h-16 w-auto object-contain logo"
						onError={(e) => {
							e.currentTarget.style.display = 'none';
						}}
						src={logoSrc}
					/>
				</Button>
			)}

			<Group>
				{partnerMetadata.isLocalizationEnabled() &&
					!runtimeConfig.isRemoteBuild && <LanguageSelect />}

				{showPartnerCta && (
					<NavigateToPartnerCta
						className="rounded-md border !border-primary-900 bg-white text-primary-900"
						onBeforeRedirect={onBeforeRedirectToPartner}
					/>
				)}

				{isProcessStarted &&
					(isMobile ? (
						<UnstyledButton
							className="border !border-primary-900 rounded-md w-7 h-7 flex justify-center items-center"
							onClick={handleReset}
						>
							<Trash />
						</UnstyledButton>
					) : (
						<Button
							className="bg-white border !border-primary-900 rounded-md"
							leftSection={<Trash />}
							onClick={handleReset}
							variant="transparent"
						>
							{localization.translate('creator', 'discard_start_over')}
						</Button>
					))}
			</Group>
		</header>
	);
};

export default Header;
